import { useEffect, useState } from "react";
import { MapPin, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/store/useApp";
import { cn } from "@/lib/utils";

/**
 * Seletor persistente da "Unidade de compra" no topo da área do cliente. A unidade escolhida dirige o
 * preço da vitrine, do carrinho e do checkout — o cliente vê o preço correto desde o início do pedido.
 *
 * Regras de UX (aprovadas):
 * - 0 unidades → nada é renderizado (preço account-level, comportamento atual).
 * - 1 unidade → auto-selecionada no store; aqui vira só um rótulo informativo.
 * - várias unidades → pede a escolha na primeira entrada (diálogo abre sozinho enquanto nada foi escolhido).
 * - depois, o cliente troca a unidade sem voltar ao checkout; o carrinho é reprecificado (itens preservados)
 *   com aviso claro.
 */
export function DeliveryUnitSwitcher() {
  const { deliveryUnits, deliveryUnitsStatus, selectedDeliveryUnitId, setSelectedDeliveryUnit } = useApp();
  const [open, setOpen] = useState(false);

  const hasUnits = deliveryUnits.length > 0;
  const mustChoose = deliveryUnits.length > 1 && !selectedDeliveryUnitId;

  // Primeira entrada com várias unidades e nenhuma escolhida: abre o diálogo para escolher antes de comprar.
  useEffect(() => {
    if (deliveryUnitsStatus === "ready" && mustChoose) setOpen(true);
  }, [deliveryUnitsStatus, mustChoose]);

  if (deliveryUnitsStatus !== "ready" || !hasUnits) return null;

  const selected = deliveryUnits.find((unit) => unit.id === selectedDeliveryUnitId) ?? null;

  const choose = async (unitId: string) => {
    const previous = selectedDeliveryUnitId;
    const unit = deliveryUnits.find((u) => u.id === unitId);
    setOpen(false);
    await setSelectedDeliveryUnit(unitId);
    // Só avisa quando TROCA de uma unidade já escolhida (evita ruído na primeira seleção).
    if (previous && previous !== unitId && unit) {
      toast.success(`Os preços do carrinho foram atualizados para a unidade ${unit.name}.`);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm transition-colors max-w-[220px]",
          mustChoose
            ? "border-primary/60 bg-primary/5 text-primary animate-pulse"
            : "border-border bg-surface hover:bg-secondary",
        )}
        title="Unidade de compra"
      >
        <MapPin className="h-4 w-4 shrink-0" />
        <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">Unidade:</span>
        <span className="truncate font-medium">
          {selected ? selected.name : "Escolher unidade"}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      </button>

      <Dialog open={open} onOpenChange={(next) => { if (!mustChoose) setOpen(next); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unidade de compra</DialogTitle>
            <DialogDescription>
              Os preços do catálogo e do carrinho seguem a unidade selecionada. Escolha a unidade antes de
              montar o pedido para ver o preço correto desde o início.
            </DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-2 pt-1">
            {deliveryUnits.map((unit) => {
              const active = unit.id === selectedDeliveryUnitId;
              return (
                <li key={unit.id}>
                  <button
                    type="button"
                    onClick={() => void choose(unit.id)}
                    className={cn(
                      "w-full text-left rounded-lg border p-3 transition-all flex items-start gap-3",
                      active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40",
                    )}
                  >
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {unit.name}
                        {unit.externalCode ? ` (${unit.externalCode})` : ""}
                      </p>
                      {(unit.address || unit.city) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {[unit.address, unit.district, unit.city].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                    {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
